import { SettingsElementType, } from '@thoughtspot/ts-chart-sdk';
import DOMPurify from 'dompurify';
import _ from 'lodash';
import { sanitizeCustomVisualProps } from '../form-builder.util';
import { isPropElement } from './custom-chart-schema-hydrator.util';
const validateClientState = (clientState) => {
    if (typeof clientState !== 'string') {
        return '';
    }
    try {
        JSON.parse(clientState);
        return clientState;
    }
    catch {
        return '';
    }
};
const SINGLE_VALUE_ELEMENTS = [
    SettingsElementType.RADIO_GROUP,
];
export const getElementValue = (element) => {
    if (isPropElement(element)) {
        const elementType = element.type;
        if (elementType !== 'section' && elementType !== 'customsort') {
            return element?.defaultValue;
        }
        return '';
    }
    switch (element.elementType) {
        case SettingsElementType.RADIO_GROUP:
            return (element?.properties?.selectedValue ??
                element?.properties?.defaultValue);
        case SettingsElementType.DROPDOWN:
            return element?.properties?.selectedOptions;
        case SettingsElementType.COLOR_PICKER:
            return element?.properties?.color ?? element?.properties?.value;
        case SettingsElementType.TOGGLE:
        case SettingsElementType.RADIO:
            return element?.properties?.checked;
        default:
            return element?.properties?.value;
    }
};
export const sanitizeSettingsElementsArray = (currentVisualProps, elements, globalPersistedVisualPropKeys, isCustomChart = false) => {
    const visualProps = {};
    const processElement = (element) => {
        if (element?.children &&
            element?.children.length &&
            !SINGLE_VALUE_ELEMENTS.includes(element.elementType)) {
            if (element.key && !element.isAnswerProperty) {
                const nestedResult = sanitizeSettingsElementsArray(currentVisualProps?.[element.key], element.children, globalPersistedVisualPropKeys, isCustomChart);
                const existingValue = _.get(visualProps, element.key);
                const ownValue = getElementValue(element) || {};
                let mergedValue = nestedResult;
                if (existingValue &&
                    _.isObject(existingValue) &&
                    _.isObject(nestedResult)) {
                    mergedValue = { ...existingValue, ...nestedResult };
                }
                if (_.isObject(ownValue)) {
                    mergedValue = { ...mergedValue, ...ownValue };
                }
                _.set(visualProps, element.key, mergedValue);
            }
            else {
                element.children.forEach(processElement);
            }
        }
        else if (element.key && !element.isAnswerProperty) {
            const defaultValue = getElementValue(element);
            const currentValue = currentVisualProps?.[element.key];
            const shouldUseDefault = currentValue === undefined ||
                currentValue === null ||
                (_.isObject(currentValue) && _.isEmpty(currentValue));
            _.set(visualProps, element.key, shouldUseDefault ? defaultValue : currentValue);
        }
    };
    elements.forEach(processElement);
    const currentVisualPropsWithPreservedState = currentVisualProps?.clientState
        ? {
            ...visualProps,
            clientState: isCustomChart
                ? DOMPurify.sanitize(currentVisualProps.clientState)
                : validateClientState(currentVisualProps.clientState),
        }
        : visualProps;
    if (!globalPersistedVisualPropKeys ||
        _.isEmpty(globalPersistedVisualPropKeys)) {
        return currentVisualPropsWithPreservedState;
    }
    globalPersistedVisualPropKeys.forEach(value => {
        if (_.get(currentVisualProps, value)) {
            _.set(currentVisualPropsWithPreservedState, value, DOMPurify.sanitize(_.get(currentVisualProps, value)));
        }
    });
    return currentVisualPropsWithPreservedState;
};
export const sanitizeCustomVisualPropsV2 = (customVisualProps, visualPropEditorDefinition, globalPersistedVisualPropKeys, isCustomChart = false) => {
    let sanitizedVisualProps = {
        columnVisualProps: {},
        axisVisualProps: {},
        dataLabelVisualProps: {},
        tooltipVisualProps: {},
        legendVisualProps: {},
        displayVisualProps: {},
    };
    const { elements, columnsVizPropDefinition, displayVizPropDefinition, dataLabelVizPropDefinition, tooltipVizPropDefinition, legendVizPropDefinition, axisVizPropDefinition, } = visualPropEditorDefinition;
    if (columnsVizPropDefinition && columnsVizPropDefinition.length) {
        sanitizedVisualProps.columnVisualProps = {};
        columnsVizPropDefinition.forEach(item => {
            Object.entries(item.columnSettingsDefinition).forEach(([colId, colDef]) => {
                const originalColumnProps = customVisualProps?.columnVisualProps?.[colId] ?? {};
                sanitizedVisualProps.columnVisualProps[colId] = sanitizeSettingsElementsArray(originalColumnProps, (colDef?.elements || []), globalPersistedVisualPropKeys, isCustomChart);
                if (originalColumnProps?.conditionalFormatting) {
                    sanitizedVisualProps.columnVisualProps[colId].conditionalFormatting =
                        originalColumnProps.conditionalFormatting;
                }
            });
        });
    }
    if (axisVizPropDefinition && axisVizPropDefinition.length) {
        sanitizedVisualProps.axisVisualProps = sanitizeSettingsElementsArray(customVisualProps?.axisVisualProps ?? {}, axisVizPropDefinition, globalPersistedVisualPropKeys, isCustomChart);
    }
    if (dataLabelVizPropDefinition && dataLabelVizPropDefinition.length) {
        sanitizedVisualProps.dataLabelVisualProps = sanitizeSettingsElementsArray(customVisualProps?.dataLabelVisualProps ?? {}, dataLabelVizPropDefinition, globalPersistedVisualPropKeys, isCustomChart);
    }
    if (tooltipVizPropDefinition && tooltipVizPropDefinition.length) {
        sanitizedVisualProps.tooltipVisualProps = sanitizeSettingsElementsArray(customVisualProps?.tooltipVisualProps ?? {}, tooltipVizPropDefinition, globalPersistedVisualPropKeys, isCustomChart);
    }
    if (legendVizPropDefinition && legendVizPropDefinition.length) {
        sanitizedVisualProps.legendVisualProps = sanitizeSettingsElementsArray(customVisualProps?.legendVisualProps ?? {}, legendVizPropDefinition, globalPersistedVisualPropKeys, isCustomChart);
    }
    if (displayVizPropDefinition && displayVizPropDefinition.length) {
        sanitizedVisualProps.displayVisualProps = sanitizeSettingsElementsArray(customVisualProps?.displayVisualProps ?? {}, displayVizPropDefinition, globalPersistedVisualPropKeys, isCustomChart);
    }
    if (customVisualProps?.clientState) {
        sanitizedVisualProps.clientState = isCustomChart
            ? DOMPurify.sanitize(customVisualProps.clientState)
            : validateClientState(customVisualProps.clientState);
    }
    if (elements && elements.length) {
        sanitizedVisualProps = {
            ...sanitizedVisualProps,
            ...sanitizeCustomVisualProps(customVisualProps, { elements }, globalPersistedVisualPropKeys),
        };
    }
    if (globalPersistedVisualPropKeys &&
        !_.isEmpty(globalPersistedVisualPropKeys)) {
        globalPersistedVisualPropKeys.forEach(key => {
            const value = _.get(customVisualProps, key);
            if (value !== undefined) {
                _.set(sanitizedVisualProps, key, DOMPurify.sanitize(value));
            }
        });
    }
    return sanitizedVisualProps;
};
//# sourceMappingURL=helpers.js.map